/**
 * TaskCreateDialog Component
 * Modal dialog for creating new tasks from the calendar view.
 * Admin can specify title, description, due date, time, and duration.
 */

import { useState, useEffect } from 'react';
import { ClipboardList } from 'lucide-react';
import { TaskSource } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCreateTask } from '@/hooks/queries';

// ============================================================================
// Types
// ============================================================================

interface TaskCreateDialogProps {
  open: boolean;
  onClose: () => void;
  initialDate?: string;  // YYYY-MM-DD, pre-fill if provided
  initialTime?: string;  // HH:mm, pre-fill if provided
}

// ============================================================================
// Component
// ============================================================================

export function TaskCreateDialog({
  open,
  onClose,
  initialDate,
  initialTime,
}: TaskCreateDialogProps) {
  // Goes through the API, not the calendar store: a store-only task is gone on
  // the next refetch or reload. The hook's optimistic item reaches the calendar
  // the same tick, so the block still appears immediately.
  const { mutate: createTask } = useCreateTask();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [duration, setDuration] = useState(60);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setDueDate(initialDate || '');
      setDueTime(initialTime || '');
      setDuration(60);
    }
  }, [open, initialDate, initialTime]);

  const handleCreate = () => {
    if (!title.trim()) return;

    createTask({
      title: title.trim(),
      description: description.trim(),
      source: TaskSource.ManagerAssigned,
      dueDate: dueDate || undefined,
      dueTime: dueDate && dueTime ? dueTime : undefined,
      durationMinutes: duration || 60,
    });

    onClose();
  };

  const isValid = title.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Create New Task
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          {/* Title - Full Width */}
          <div className="col-span-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title..."
              autoFocus
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Description - Full Width */}
          <div className="col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter task description..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            />
          </div>

          {/* Due Date */}
          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1.5">
              Due Date
            </label>
            <input
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Due Time - Only shown when date is set */}
          {dueDate && (
            <div>
              <label htmlFor="dueTime" className="block text-sm font-medium text-gray-700 mb-1.5">
                Due Time
              </label>
              <input
                id="dueTime"
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          )}

          {/* Duration */}
          <div className={dueDate ? '' : 'col-span-1'}>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1.5">
              Duration
            </label>
            <div className="relative">
              <input
                id="duration"
                type="number"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                min={15}
                max={480}
                step={15}
                className="w-full px-3 py-2 pr-16 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                minutes
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!isValid}>
            Create Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TaskCreateDialog;
